/**
 * PrimarisProvider - Injects semantic color CSS variables from @animus/palette
 *
 * Wrap your app with this provider to enable Primaris color tokens.
 * Supports dark mode via the `mode` prop or system preference.
 */

import { useEffect, useState } from "react";

import { palette } from "@animus/palette";

import { type AnimationMode, AnimationProvider } from "./animation";

type ColorMode = "light" | "dark" | "system";

interface PrimarisProviderProps {
  children: React.ReactNode;
  mode?: ColorMode;
  animationMode?: AnimationMode;
}

/**
 * Hook to detect system color scheme preference
 */
function useSystemColorMode(): "light" | "dark" {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") {
      return "dark";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setMode(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return mode;
}

/**
 * Dark theme color mappings
 *
 * Uses Animus brand colors: warm espresso backgrounds, pink primary.
 */
const darkTheme = {
  // Backgrounds (warm espresso, never pure black)
  "--primaris-background": palette.brand.dark.bg,
  "--primaris-background-subtle": palette.brand.dark.card,
  "--primaris-background-muted": palette.brand.dark.border,

  // Foregrounds (cream tones for warmth)
  "--primaris-foreground": palette.brand.cream[50],
  "--primaris-foreground-muted": palette.brand.cream[300],
  "--primaris-foreground-subtle": palette.neutral[500],

  // Cards
  "--primaris-card": palette.brand.dark.card,
  "--primaris-card-foreground": palette.brand.cream[50],

  // Borders
  "--primaris-border": palette.brand.dark.border,
  "--primaris-border-subtle": "rgba(255, 255, 255, 0.08)",

  // Inputs
  "--primaris-input": palette.brand.dark.card,
  "--primaris-input-foreground": palette.brand.cream[50],
  "--primaris-ring": palette.brand.pink[400],

  // Primary (Strawberry Glaze pink)
  "--primaris-primary": palette.brand.pink[400],
  "--primaris-primary-foreground": palette.brand.dark.bg,
  "--primaris-primary-hover": palette.brand.pink[300],

  // Secondary
  "--primaris-secondary": palette.brand.dark.border,
  "--primaris-secondary-foreground": palette.brand.cream[100],

  // Muted
  "--primaris-muted": "rgba(255, 255, 255, 0.08)",
  "--primaris-muted-foreground": palette.brand.cream[300],

  // Accent (subtle pink tint)
  "--primaris-accent": "rgba(232, 136, 153, 0.15)",
  "--primaris-accent-foreground": palette.brand.pink[300],

  // Semantic states
  "--primaris-destructive": palette.error.DEFAULT,
  "--primaris-destructive-foreground": palette.neutral[0],

  "--primaris-success": palette.success.DEFAULT,
  "--primaris-success-foreground": palette.neutral[0],

  "--primaris-warning": palette.warning.DEFAULT,
  "--primaris-warning-foreground": palette.neutral[950],

  "--primaris-info": palette.brand.pink[400],
  "--primaris-info-foreground": palette.brand.dark.bg,

  // Glassmorphism - warm tint for vibrancy effect
  "--primaris-glass-surface": "rgba(26, 22, 20, 0.32)",
  "--primaris-glass-surface-subtle": "rgba(26, 22, 20, 0.24)",
  "--primaris-glass-surface-elevated": "rgba(26, 22, 20, 0.48)",
  "--primaris-glass-border": "rgba(255, 243, 232, 0.12)",
  "--primaris-glass-border-subtle": "rgba(255, 243, 232, 0.06)",

  // Component-specific
  "--primaris-avatar-inset": "rgba(255, 255, 255, 0.25)",
} as const;

/**
 * Light theme color mappings
 *
 * Uses Animus brand colors: vanilla cream backgrounds, pink primary.
 */
const lightTheme = {
  // Backgrounds (vanilla cream)
  "--primaris-background": palette.brand.cream[100],
  "--primaris-background-subtle": palette.brand.cream[200],
  "--primaris-background-muted": palette.brand.cream[300],

  // Foregrounds
  "--primaris-foreground": palette.brand.dark.card,
  "--primaris-foreground-muted": palette.neutral[600],
  "--primaris-foreground-subtle": palette.neutral[500],

  // Cards
  "--primaris-card": palette.brand.cream[50],
  "--primaris-card-foreground": palette.brand.dark.card,

  // Borders
  "--primaris-border": palette.brand.cream[300],
  "--primaris-border-subtle": palette.brand.cream[200],

  // Inputs
  "--primaris-input": palette.brand.cream[50],
  "--primaris-input-foreground": palette.brand.dark.card,
  "--primaris-ring": palette.brand.pink[400],

  // Primary (Strawberry Glaze pink)
  "--primaris-primary": palette.brand.pink[500],
  "--primaris-primary-foreground": palette.neutral[0],
  "--primaris-primary-hover": palette.brand.pink[600],

  // Secondary
  "--primaris-secondary": palette.brand.cream[200],
  "--primaris-secondary-foreground": palette.brand.dark.card,

  // Muted
  "--primaris-muted": palette.brand.cream[200],
  "--primaris-muted-foreground": palette.neutral[500],

  // Accent (subtle pink tint)
  "--primaris-accent": palette.brand.pink[100],
  "--primaris-accent-foreground": palette.brand.pink[600],

  // Semantic states
  "--primaris-destructive": palette.error.DEFAULT,
  "--primaris-destructive-foreground": palette.neutral[0],

  "--primaris-success": palette.success.DEFAULT,
  "--primaris-success-foreground": palette.neutral[0],

  "--primaris-warning": palette.warning.DEFAULT,
  "--primaris-warning-foreground": palette.neutral[950],

  "--primaris-info": palette.brand.pink[500],
  "--primaris-info-foreground": palette.neutral[0],

  // Glassmorphism - warm cream tint for vibrancy effect
  "--primaris-glass-surface": "rgba(255, 249, 244, 0.7)",
  "--primaris-glass-surface-subtle": "rgba(255, 249, 244, 0.6)",
  "--primaris-glass-surface-elevated": "rgba(255, 249, 244, 0.82)",
  "--primaris-glass-border": "rgba(42, 36, 34, 0.1)",
  "--primaris-glass-border-subtle": "rgba(42, 36, 34, 0.06)",

  // Component-specific
  "--primaris-avatar-inset": "rgba(0, 0, 0, 0.1)",
} as const;

const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

export function PrimarisProvider({
  children,
  mode = "system",
  animationMode = "system",
}: PrimarisProviderProps) {
  const systemMode = useSystemColorMode();
  const resolvedMode = mode === "system" ? systemMode : mode;
  const theme = themes[resolvedMode];

  // Set CSS variables on :root so StyleX defineVars references resolve correctly
  useEffect(() => {
    const root = document.documentElement;

    // Set data attribute for CSS selectors
    root.dataset.theme = resolvedMode;
    root.classList.toggle("dark", resolvedMode === "dark");

    for (const [key, value] of Object.entries(theme)) {
      root.style.setProperty(key, value);
    }

    return () => {
      for (const key of Object.keys(theme)) {
        root.style.removeProperty(key);
      }
      delete root.dataset.theme;
      root.classList.remove("dark");
    };
  }, [theme, resolvedMode]);

  return <AnimationProvider mode={animationMode}>{children}</AnimationProvider>;
}

export { darkTheme, lightTheme, themes };
export type { ColorMode, PrimarisProviderProps };
